import axios from "axios";
import config from "./ApiforContext";
import { notification } from "antd";
import {
  clearSessionProfile,
  getProfile,
  setSessionProfile,
} from "./profile";

/** Legacy keys — scrubbed on login/logout; no longer written for identity. */
const PROFILE_STORAGE_KEYS = [
  "userId",
  "email",
  "tenantId",
  "userName",
  "user",
  "firmName",
  "firmId",
  "menu",
  "isSubFirm",
  "usePaymentSet",
  "tenantLogo",
];

const LEGACY_TOKEN_KEYS = [
  "expiration",
  "token",
  "refreshExpiration",
  "refreshToken",
];

const CSRF_STORAGE_KEY = "fnk_csrf_client";
const REFRESH_TOKEN_KEY = "fnk_rt_fallback";

/** iOS tüm tarayıcılar WebKit kullanır (ITP aktif). Desktop'ta sadece Safari. */
const isSafariOrIOS = (() => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isDesktopSafari =
    /Safari/.test(ua) && !/Chrome/.test(ua) && !/Chromium/.test(ua) && !/Android/.test(ua);
  return isIOS || isDesktopSafari;
})();

/** Access token sadece bellekte tutulur; sayfa kapanınca yok olur. */
let inMemoryAccessToken = null;

const isObject = (value) =>
  typeof value === "object" && !Array.isArray(value) && value !== null;

const getCookie = (name) => {
  const match = document.cookie.match(
    new RegExp(
      "(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"
    )
  );
  return match ? decodeURIComponent(match[1]) : null;
};

const resolveCsrfToken = () => {
  try {
    return (
      getCookie("fnk_csrf") ||
      sessionStorage.getItem(CSRF_STORAGE_KEY) ||
      null
    );
  } catch (e) {
    return getCookie("fnk_csrf");
  }
};

const persistCsrfToken = (csrfToken) => {
  if (!csrfToken) return;
  try {
    sessionStorage.setItem(CSRF_STORAGE_KEY, csrfToken);
  } catch (e) {
    // ignore
  }
};

const clearCsrfToken = () => {
  try {
    sessionStorage.removeItem(CSRF_STORAGE_KEY);
  } catch (e) {
    // ignore
  }
};

const storeTokens = (accessToken, refreshToken) => {
  if (!isSafariOrIOS) return;
  inMemoryAccessToken = accessToken || null;
  if (refreshToken) {
    try {
      sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    } catch (e) {
      // ignore
    }
  }
};

const getAccessToken = () => (isSafariOrIOS ? inMemoryAccessToken : null);

const getRefreshToken = () => {
  if (!isSafariOrIOS) return null;
  try {
    return sessionStorage.getItem(REFRESH_TOKEN_KEY);
  } catch (e) {
    return null;
  }
};

const clearTokens = () => {
  inMemoryAccessToken = null;
  try {
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch (e) {
    // ignore
  }
};

const handleDefaultNotification = (response) => {
  let type = response.data.success ? "success" : "error";
  let message = response.data.success
    ? "İşlem başarılı."
    : response.data.message;

  notification[type]({
    message: message,
    duration: 3,
  });
};

let refreshPromise = null;

const refreshSession = () => {
  if (!refreshPromise) {
    const refreshHeaders = {
      "Content-Type": "application/json",
    };
    const csrf = resolveCsrfToken();
    if (csrf) refreshHeaders["X-CSRF-Token"] = csrf;

    if (isSafariOrIOS) {
      const rt = getRefreshToken();
      if (rt) refreshHeaders["X-Refresh-Token"] = rt;
      const at = getAccessToken();
      if (at) refreshHeaders["Authorization"] = `Bearer ${at}`;
    }

    refreshPromise = axios
      .request({
        method: "post",
        url: config.apiUrl + "/FirmAuth/RefreshToken",
        withCredentials: true,
        headers: refreshHeaders,
      })
      .then((response) => {
        const payload = response?.data?.data ?? response?.data;
        if (payload) {
          RequestHandler.setProfile(payload);
        }
        return response;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

const buildHeaders = (method, extraHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...extraHeaders,
  };

  const token = getAccessToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const unsafe = ["post", "put", "patch", "delete"].includes(
    (method || "").toLowerCase()
  );
  if (unsafe) {
    const csrf = resolveCsrfToken();
    if (csrf) {
      headers["X-CSRF-Token"] = csrf;
    }
  }

  return headers;
};

const redirectToSignIn = () => {
  RequestHandler.removeUserInfo();
  if (window.location.pathname.toLowerCase().startsWith("/signin")) {
    return;
  }
  window.location = "/signin";
};

const handleError = (error, options, retry) => {
  let message = error.toString();

  if (error.response) {
    if (error.response.status === 406) {
      error.response.data.data.forEach((validationMessage) => {
        notification.error({
          message: validationMessage,
          duration: 3,
        });
      });
    } else if (error.response.status === 400) {
      message = isObject(error.response.data)
        ? error.response.data.message
        : error.response.data;

      notification.error({
        message: message,
        duration: 3,
      });
    } else if (error.response.status === 401) {
      if (options?.skipAuthRedirect) {
        return options?.rawResponse ? error : error.response?.data;
      }

      if (!options?._retried && !options?.skipRefresh) {
        return refreshSession()
          .then(() => retry())
          .catch(() => {
            redirectToSignIn();
          });
      }

      redirectToSignIn();
      return;
    } else if (error.response.status === 403) {
      if (
        typeof error.response.data === "string" &&
        error.response.data.toLowerCase().includes("csrf")
      ) {
        notification.error({
          message: "CSRF doğrulaması başarısız. Lütfen sayfayı yenileyin.",
          duration: 3,
        });
        return;
      }
      if (error.response.data?.message) {
        notification.error({
          message: error.response.data.message,
          duration: 3,
        });
        return;
      }
      redirectToSignIn();
      return;
    } else if (error.response.status === 500) {
      notification.error({
        message: isObject(error.response.data)
          ? error.response.data.message
          : "Şu anda işleminizi gerçekleştiremiyoruz.Lütfen daha sonra tekrar deneyiniz.",
        duration: 3,
      });
    } else {
      notification.error({
        message: error.response.data?.message
          ? error.response.data.message
          : message,
        duration: 3,
      });
    }
  }

  if (options?.rawResponse) {
    return error;
  }

  return error.response
    ? error.response.data
    : {
        message: message,
        success: false,
      };
};

const handleRequest = (
  endPoint,
  method,
  data,
  showDefaultNotification,
  options
) => {
  const mergedOptions = options ? { ...options } : {};
  let defaultOptions = {
    method: method,
    url: endPoint.startsWith("http") ? endPoint : config.apiUrl + endPoint,
    headers: buildHeaders(method, mergedOptions.headers),
    data: method !== "get" ? data : undefined,
    params: method === "get" ? data : undefined,
    withCredentials: true,
  };

  if (mergedOptions) {
    const { headers, ...rest } = mergedOptions;
    defaultOptions = Object.assign(defaultOptions, rest);
    if (headers) {
      defaultOptions.headers = buildHeaders(method, headers);
    }
  }

  const retry = () =>
    handleRequest(endPoint, method, data, showDefaultNotification, {
      ...mergedOptions,
      _retried: true,
    });

  return axios
    .request(defaultOptions)
    .then((response) => {
      if (showDefaultNotification) {
        handleDefaultNotification(response);
      }

      return mergedOptions?.rawResponse ? response : response.data;
    })
    .catch((error) => handleError(error, mergedOptions, retry));
};

const RequestHandler = {
  send: (endPoint, method, data, showDefaultNotification, options) => {
    return handleRequest(
      endPoint,
      method,
      data,
      showDefaultNotification,
      options
    );
  },

  get: (endPoint, data, options) =>
    RequestHandler.send(endPoint, "get", data, false, options),

  post: (endPoint, data, showDefaultNotification, options) =>
    RequestHandler.send(
      endPoint,
      "post",
      data,
      showDefaultNotification !== false ? true : false,
      options
    ),

  put: (endPoint, data, showDefaultNotification, options) =>
    RequestHandler.send(
      endPoint,
      "put",
      data,
      showDefaultNotification !== false ? true : false,
      options
    ),

  delete: (endPoint, data, showDefaultNotification, options) =>
    RequestHandler.send(
      endPoint,
      "delete",
      data,
      showDefaultNotification !== false ? true : false,
      options
    ),

  getCsrfToken: () => resolveCsrfToken(),

  uploadProps: (extraHeaders = {}) => {
    const headers = { ...extraHeaders };
    const csrf = resolveCsrfToken();
    if (csrf) {
      headers["X-CSRF-Token"] = csrf;
    }
    const token = getAccessToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return {
      withCredentials: true,
      headers,
    };
  },

  /** Cookie oturumu ile dosya indirme (cross-origin window.open yerine). */
  download: (endPoint) => {
    const url = endPoint.startsWith("http")
      ? endPoint
      : config.apiUrl + endPoint;
    const dlHeaders = {};
    const token = getAccessToken();
    if (token) dlHeaders["Authorization"] = `Bearer ${token}`;
    return axios
      .get(url, { withCredentials: true, responseType: "blob", headers: dlHeaders })
      .then((response) => {
        const disposition = response.headers["content-disposition"];
        let fileName = "download";
        if (disposition) {
          const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
            disposition
          );
          if (match?.[1]) fileName = match[1].replace(/['"]/g, "");
        }
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      });
  },


  getToken: () => null,

  getProfileSnapshot: () => getProfile(),

  setUserInfo: (response) => {
    const payload = response?.data ?? response;
    RequestHandler.setProfile(payload);
    RequestHandler.clearLegacyAuthStorage();
  },

  setProfile: (profile) => {
    if (!profile) return;
    setSessionProfile(profile);
    PROFILE_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    if (profile.csrfToken) persistCsrfToken(profile.csrfToken);
    storeTokens(profile.token, profile.refreshToken);
  },

  removeUserInfo: () => {
    clearSessionProfile();
    [...PROFILE_STORAGE_KEYS, ...LEGACY_TOKEN_KEYS].forEach((key) =>
      localStorage.removeItem(key)
    );
    clearCsrfToken();
    clearTokens();
  },

  clearLegacyAuthStorage: () => {
    [...PROFILE_STORAGE_KEYS, ...LEGACY_TOKEN_KEYS].forEach((key) =>
      localStorage.removeItem(key)
    );
  },

  fetchMe: () =>
    RequestHandler.get("/FirmAuth/Me", undefined, {
      skipRefresh: true,
      skipAuthRedirect: true,
    }),
};

export default RequestHandler;
